// ─── Thesis Approval Overview ─────────────────────────────────────────────────
// Per-chapter editorial status summary with thesis-level readiness indicators.
// Designed for the export sidebar and review dashboard.

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, CheckCircle2, Lock, Clock, Eye, FileEdit, AlertCircle, BarChart3 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useChapterStore } from '../store/chapterStore'

// ── Status icon + colour ──────────────────────────────────────────────────────

const STATUS_CFG = {
  LOCKED:       { icon: Lock,         color: '#7c3aed', label: 'Submitted',    ready: true  },
  APPROVED:     { icon: CheckCircle2, color: '#16a34a', label: 'Approved',     ready: true  },
  FINALIZED:    { icon: CheckCircle2, color: '#15803d', label: 'Finalised',    ready: true  },
  UNDER_REVIEW: { icon: Eye,          color: '#d97706', label: 'Under Review', ready: false },
  REVIEW:       { icon: Eye,          color: '#d97706', label: 'Under Review', ready: false },
  DRAFT:        { icon: FileEdit,     color: '#2563eb', label: 'Draft',        ready: false },
  GENERATING:   { icon: Loader2,      color: '#2563eb', label: 'Generating',   ready: false },
  NOT_STARTED:  { icon: Clock,        color: '#94a3b8', label: 'Not Started',  ready: false },
}

function statusCfg(status) {
  return STATUS_CFG[status] || STATUS_CFG.DRAFT
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function ReadinessBar({ pct }) {
  const color = pct >= 80 ? '#059669' : pct >= 50 ? '#d97706' : '#2563eb'
  return (
    <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden', flex: 1 }}>
      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.6s ease' }} />
    </div>
  )
}

// ── Chapter row ───────────────────────────────────────────────────────────────

function ChapterRow({ chapter, thesisId, unresolvedComments = 0 }) {
  const cfg  = statusCfg(chapter.status)
  const Icon = cfg.icon

  return (
    <Link
      to={`/app/thesis/${thesisId}/chapters/${chapter.id}`}
      style={{ display: 'block', padding: '0.6rem 0.75rem', borderRadius: 8, marginBottom: 4, border: '1px solid #f1f5f9', textDecoration: 'none', background: '#fff', transition: 'border-color 0.15s' }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = '#dde4ef')}
      onMouseLeave={e => (e.currentTarget.style.borderColor = '#f1f5f9')}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon style={{ width: 12, height: 12, color: cfg.color, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Ch. {chapter.chapter_number}: {chapter.title}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 }}>
            <span style={{ fontSize: 10, color: cfg.color, fontWeight: 600 }}>{cfg.label}</span>
            {chapter.approved_at && (
              <span style={{ fontSize: 10, color: '#94a3b8' }}>Approved {formatDate(chapter.approved_at)}</span>
            )}
            {chapter.last_validated_at && !chapter.approved_at && (
              <span style={{ fontSize: 10, color: '#94a3b8' }}>Reviewed {formatDate(chapter.last_validated_at)}</span>
            )}
            {unresolvedComments > 0 && (
              <span style={{ fontSize: 10, color: '#d97706', fontWeight: 600 }}>
                {unresolvedComments} comment{unresolvedComments > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: '#64748b', margin: 0 }}>
            {(chapter.word_count || 0).toLocaleString()}
          </p>
          <p style={{ fontSize: 9, color: '#94a3b8', margin: 0 }}>words</p>
        </div>
      </div>
    </Link>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * @param {{ thesisId: string, chapters: Array, compact?: boolean }} props
 */
export default function ThesisApprovalOverview({ thesisId, chapters = [], compact = false }) {
  const [commentCounts, setCommentCounts] = useState({})

  // Overlay live status from store so transitions in the editor propagate here instantly
  const storeChapters = useChapterStore(state => state.chapters)

  useEffect(() => {
    if (!thesisId) return
    supabase
      .from('chapter_review_comments')
      .select('chapter_id, resolved')
      .eq('thesis_id', thesisId)
      .eq('resolved', false)
      .then(({ data }) => {
        if (!data) return
        const counts = {}
        for (const row of data) {
          counts[row.chapter_id] = (counts[row.chapter_id] || 0) + 1
        }
        setCommentCounts(counts)
      })
      .catch(() => {}) // table may not exist yet
  }, [thesisId])

  const sorted = [...chapters]
    .map(ch => ({ ...ch, status: storeChapters[ch.id]?.status || ch.status }))
    .sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0))
  const readyChapters  = sorted.filter(c => statusCfg(c.status).ready)
  const totalWords     = sorted.reduce((s, c) => s + (c.word_count || 0), 0)
  const readinessPct   = sorted.length > 0 ? Math.round((readyChapters.length / sorted.length) * 100) : 0
  const unresolvedTotal= Object.values(commentCounts).reduce((s, n) => s + n, 0)

  if (compact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 11, color: '#64748b', margin: '0 0 3px' }}>
            {readyChapters.length}/{sorted.length} chapters approved
          </p>
          <ReadinessBar pct={readinessPct} />
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: readinessPct === 100 ? '#059669' : '#64748b' }}>
          {readinessPct}%
        </span>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Thesis-level readiness summary */}
      <div style={{ padding: '0.75rem 1rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Export Readiness
          </span>
          <span style={{ fontSize: 13, fontWeight: 800, color: readinessPct === 100 ? '#059669' : '#d97706' }}>
            {readinessPct}%
          </span>
        </div>
        <ReadinessBar pct={readinessPct} />

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '0.4rem', marginTop: 8 }}>
          {[
            { label: 'Approved', value: readyChapters.length, icon: '✓' },
            { label: 'Words',    value: totalWords > 0 ? (totalWords / 1000).toFixed(1) + 'k' : '0', icon: '≡' },
            { label: 'Comments', value: unresolvedTotal, icon: '◉' },
          ].map(({ label, value, icon }) => (
            <div key={label} style={{ textAlign: 'center', background: '#fff', borderRadius: 6, padding: '0.3rem', border: '1px solid #e2e8f0' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#334155', margin: 0 }}>{icon} {value}</p>
              <p style={{ fontSize: 9, color: '#94a3b8', margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Per-chapter list */}
      <div style={{ padding: '0.75rem', flex: 1 }}>
        {sorted.map(ch => (
          <ChapterRow
            key={ch.id}
            chapter={ch}
            thesisId={thesisId}
            unresolvedComments={commentCounts[ch.id] || 0}
          />
        ))}
      </div>
    </div>
  )
}
